import {
  ILanguage,
  IItems,
  IMenuItem,
  INewMenuItem,
  IFilterData,
  ISystemItems,
  IMenuData,
} from "../@types/types";
import settings from "../../package.json";

export const requestERPLYData = async (
  requestData: Record<string, unknown>
) => {
  const url = `https://${requestData.clientCode}.erply.com/api/`;
  const requestFormData = new FormData();
  Object.entries(requestData).forEach((fieldValue) => {
    const [key, value] = fieldValue;
    requestFormData.set(`${key}`, `${value}`);
  });

  const response = await fetch(url, {
    method: "POST",
    body: requestFormData,
  });

  return await response.json();
};

export const createLanguageData = (
  languages: ILanguage[],
  link: string,
  accountLang: string,
  lastPage: string
) => {
  const langs: ILanguage[] = [];

  languages.forEach((lang) => {
    let targetUrl = "";
    if (lastPage) {
      targetUrl = lastPage.replace(
        `=${accountLang}`,
        `=${lang.legacyIdentifier}`
      );
    } else {
      targetUrl = link.replace(`=${accountLang}`, `=${lang.legacyIdentifier}`);
    }

    const isActive = accountLang === lang.legacyIdentifier ? true : false;
    langs.push({ ...lang, targetUrl, isActive });
  });

  return langs;
};

export const colors = [
  "#05d24b",
  "#2196F3",
  "#00D1B2",
  "#D1C51B",
  "#FF3860",
  "#BA2F2A",
  "#600B6C",
  "#DD6EDD",
  "#70A14F",
  "#507181",
  "#AA5083",
  "#20CDD3",
  "#33E129",
  "#BF4680",
  "#50D2F0",
  "#F03272",
  "#5A69A2",
  "#EA286A",
  "#5AC688",
  "#B9975B",
  "#282528",
  "#FFDD57",
  "#B524D5",
  "#AA349F",
  "#30885A",
  "#572571",
  "#143A21",
  "#0E8DF0",
  "#7E4D5D",
  "#827146",
  "#7B4E83",
  "#7B05A4",
  "#292772",
  "#B34266",
  "#9B06D1",
  "#FB3130",
  "#346568",
  "#13182D",
  "#32052B",
  "#C5628B",
];

export const flatenArray = (data: IItems[]) => {
  const newArray: Pick<
    IMenuItem,
    "title" | "targetType" | "targetUrl" | "iconClass"
  >[] = [];

  const filterData: IFilterData = {
    quickLinks: [],
    mainItems: [],
  };

  data.forEach((item) => {
    const {
      title,
      menu,
      targetUrl,
      isGroup,
      isVisible,
      targetType,
      iconClass,
    } = item;
    if (isVisible) {
      newArray.push({ title, targetUrl, targetType, iconClass });
      if (isGroup && menu) {
        const { additionalLinks, menuGroups, quickLinks } = menu;
        if (additionalLinks && additionalLinks?.length) {
          additionalLinks.forEach((link) => {
            if (link.isVisible) {
              newArray.push({
                title: link.title,
                targetUrl: link.targetUrl,
                targetType: link.targetType,
                iconClass: link.iconClass,
              });

              filterData.mainItems.push({
                title: link.title,
                targetUrl: link.targetUrl,
                targetType: link.targetType,
                iconClass: link.iconClass,
                breadCrumbs: title,
              });
            }
          });
        }

        if (quickLinks && quickLinks?.length) {
          quickLinks.forEach((link) => {
            if (link.isVisible) {
              newArray.push({
                title: link.title,
                targetUrl: link.targetUrl,
                targetType: link.targetType,
                iconClass: link.iconClass,
              });
              filterData.quickLinks.push({
                title: link.title,
                targetUrl: link.targetUrl,
                targetType: link.targetType,
                iconClass: link.iconClass,
              });
            }
          });
        }

        if (menuGroups && menuGroups?.length) {
          menuGroups.forEach((group) => {
            const { items, isVisible, title: groupTitle } = group;
            if (isVisible && items?.length) {
              items.forEach((item) => {
                const {
                  title: itemTitle,
                  targetUrl,
                  isVisible,
                  targetType,
                  iconClass,
                } = item;
                if (isVisible) {
                  newArray.push({ title, targetUrl, targetType, iconClass });
                  filterData.mainItems.push({
                    title: itemTitle,
                    targetUrl: targetUrl,
                    targetType: targetType,
                    iconClass: iconClass,
                    breadCrumbs: `${title} - ${groupTitle}`,
                  });
                }
              });
            }
          });
        }
      }
    }
  });

  return filterData;
};

export const addNewMenuItem = (menuItems: IItems[], newItem: INewMenuItem) => {
  const newArray: IItems[] = [...menuItems];

  if (newItem && newArray?.length) {
    const { item, parentId } = newItem;

    if (parentId) {
      const isParent = newArray?.find((item) => item.id === parentId);
      const index = newArray?.findIndex((item) => item.id === parentId);
      if (isParent && isParent.menu) {
        const { menu } = isParent;
        if (menu.additionalLinks) {
          const isAddedAlready = menu.additionalLinks?.find(
            (link) => link.id === item.id
          );
          if (isAddedAlready) {
            return newArray;
          } else {
            const target = newArray[index].menu;
            if (target) {
              target.additionalLinks = [...menu.additionalLinks, item];
              newArray[index].menu = target;
              return newArray;
            }
          }
        } else {
          const target = newArray[index].menu;
          if (target) {
            target.additionalLinks = [item];
            newArray[index].menu = target;
            return newArray;
          }
        }
      } else {
        item.menu = null;
        newArray.push(item);
        return newArray;
      }
    } else {
      const isAddedAlready = newArray?.find((target) => target.id === item.id);
      if (isAddedAlready) {
        return newArray;
      } else {
        item.menu = null;
        newArray.push(item);
        return newArray;
      }
    }
  }
};

export const addItemsInBulk = (
  savedItems: INewMenuItem[],
  menuItems: IMenuData
) => {
  const menuData = JSON.parse(JSON.stringify(menuItems)) as IMenuData;
  savedItems.forEach((item) => {
    const items = addNewMenuItem(menuData.items, item);
    if (items) {
      menuData.items = items;
    }
  });

  return menuData;
};

export const getFirstTwoChars = (value: string) => {
  return value.substring(0, 2).toUpperCase();
};

export const filterItems = (array: ISystemItems[], infoGroups: string[]) => {
  const result: {
    infoGroup: ISystemItems[];
    settingsGroup: ISystemItems[];
    authGroup: ISystemItems[];
  } = {
    infoGroup: [],
    settingsGroup: [],
    authGroup: [],
  };

  array.forEach((el) => {
    if (infoGroups.includes(el.id)) {
      result.infoGroup.push({ ...el });
    } else if (["sys-menu-clock-in-out", "sys-menu-logout"].includes(el.id)) {
      result.authGroup.push({ ...el });
    } else {
      result.settingsGroup.push({ ...el });
    }
  });

  return result;
};

export const addActiveClassname = (
  className: string,
  isActive: boolean,
  activeClassName: string
): string => {
  if (isActive) {
    return `${className} ${activeClassName}`;
  }
  return className;
};

export const handleFilter = (data: IFilterData, filterValue: string) => {
  const { quickLinks, mainItems } = data;
  const result: IFilterData = {
    quickLinks: [],
    mainItems: [],
  };
  const quickLinksResult = [...quickLinks].filter((e) =>
    e.title.toLowerCase().includes(filterValue.toLowerCase())
  );

  const mainItemsResult = [...mainItems].filter((e) =>
    e.title.toLowerCase().includes(filterValue.toLowerCase())
  );

  result.quickLinks = quickLinksResult;
  result.mainItems = mainItemsResult;

  return result;
};

export const getReleaseInfo = () => settings.version;

export const createStyle = () => {
  const sheet = document.createElement("style");
  sheet.innerHTML = `
    
  @font-face {
    font-family: "ErplyLadna";
    src: url(https://assets.erply.com/global/fonts/erply-ladna/WOFF/ErplyLadna-Light.woff)
      format("woff");
    font-display: swap;
    font-weight: 70;
  }

  @font-face {
    font-family: "ErplyLadna";
    src: url(https://assets.erply.com/global/fonts/erply-ladna/WOFF/ErplyLadna-Regular.woff)
      format("woff");
    font-display: swap;
    font-weight: 96;
  }
 
 
    
    `;
  document.body.appendChild(sheet);
};

export const handleLinkClick = (href: string, isBlank: boolean) => {
  if (isBlank) {
    Object.assign(document.createElement("a"), {
      target: "_blank",
      rel: "noopener noreferrer",
      href: href,
    }).click();
  } else {
    window.location.href = href;
  }
};
